/**
 * YearOfBirthSelect Component
 *
 * Dropdown selector for year of birth from 1900 to (current year - 18)
 * Ensures users are at least 18 years old
 */

import React from "react";
import { cn } from "../utils/cn";

export interface YearOfBirthSelectProps {
  /** Current selected year value */
  value: number | null;
  /** Handler for value changes */
  onChange: (year: number | null) => void;
  /** Handler for blur event */
  onBlur?: () => void;
  /** Error message to display */
  error?: string;
  /** HTML id attribute */
  id?: string;
  /** Whether the select is disabled */
  disabled?: boolean;
}

/**
 * YearOfBirthSelect - Dropdown for selecting birth year
 *
 * Generates options from 1900 to (current year - 18) to ensure
 * all users are at least 18 years old.
 *
 * @param props - Component props
 * @returns Year of birth select dropdown
 */
export const YearOfBirthSelect: React.FC<YearOfBirthSelectProps> = ({
  value,
  onChange,
  onBlur,
  error,
  id = "yearOfBirth",
  disabled = false,
}) => {
  const currentYear = new Date().getFullYear();
  const maxYear = currentYear - 18; // Ensure minimum age of 18
  const minYear = 1900;

  // Generate year options from newest to oldest
  const years: number[] = [];
  for (let year = maxYear; year >= minYear; year--) {
    years.push(year);
  }

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = e.target.value;
    onChange(selectedValue === "" ? null : parseInt(selectedValue, 10));
  };

  return (
    <div className="form-select-wrapper">
      <select
        id={id}
        name="yearOfBirth"
        value={value ?? ""}
        onChange={handleChange}
        onBlur={onBlur}
        className={cn("form-select", error && "form-select-error")}
        disabled={disabled}
        required
        aria-invalid={error ? "true" : "false"}
        aria-describedby={error ? `${id}-error` : undefined}
      >
        <option value="">Please Select</option>
        {years.map((year) => (
          <option key={year} value={year}>
            {year}
          </option>
        ))}
      </select>
      {error && (
        <p id={`${id}-error`} className="form-error-message" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};

export default YearOfBirthSelect;
